import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import Canvas from "./Canvas";
import { objToArray } from "./Utils";
import { clamp } from "./Math";
import Format from "./models/format.model";

// Events
import EventManager from "./EventManager";

// UI
import {
  MdGridOn,
  MdGridOff,
  MdNotInterested,
  MdFeaturedVideo,
  MdImage,
  MdViewQuilt
} from "react-icons/md";

// Images
import heroOverlay from "./assets/hero_overlay.png";
import portraitOverlay from "./assets/portrait_overlay.png";
import searchOverlay from "./assets/search_overlay.png";

const StyledContainer = styled.div`
  width: 100%;
  margin: 4.8rem auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Pagination = styled.ul`
  display: inline-flex;
  padding: 0;
  margin: 0;
  list-style: none;
  justify-content: center;
  align-items: center;
`;

const Bullet = styled.li`
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 0.6rem;
  background-color: #eee;
  margin: 0.6rem;
  text-decoration: none;

  &.active {
    background-color: var(--blue);
  }
`;

const Title = styled.h3`
  display: inline-flex;
  margin: 0;
  height: 4.8rem;
  margin: 2.4rem;
  align-items: center;
  position: relative;
  color: #666;

  .icon {
    fill: #ccc;
    margin-right: 1.2rem;
  }

  &:after {
    margin: 0 0 0 1.2rem;
    content: "";
    width: 0.8rem;
    height: 0.8rem;
    background: limegreen;
    border-radius: 0.4rem;
  }
`;

const PreviewContainer = styled.div`
  width: 80vw;
  margin: 0 auto;
  min-height: calc(100vh - 19.2rem);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-evenly;

  > * {
    margin: 2.4rem;
  }
`;

const StyledActions = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  border-radius: 0.8rem;
  box-shadow: 0 1rem 1.6rem 0 rgba(0, 0, 0, 0.12);
  padding: 1.2rem;
  top: 50%;
  left: 2.4rem;
  z-index: 24;
  background-color: rgba(210, 210, 210, 0.24);
  backdrop-filter: blur(10px);
  overflow: hidden;
  transform: translate(0, -50%);
`;

const email_avatar = {
  HERO: Format.create({
    label: "",
    width: 48,
    height: 48,
    borderRadius: [24],
    grid: {
      rows: 24,
      columns: 24
    },
    guides: {
      safearea: [24, 64, 720, 284]
    },
    overlay: heroOverlay
  })
};

const tiles = {
  HERO: Format.create({
    label: "hero",
    width: 768,
    height: 496,
    grid: {
      rows: 165.333,
      columns: 256
    },
    guides: {
      safearea: [24, 64, 720, 284]
    },
    overlay: heroOverlay
  }),
  PORTRAIT: Format.create({
    label: "portrait",
    width: 288,
    height: 464,
    grid: {
      rows: 154.666667,
      columns: 96
    },
    guides: {
      safearea: [16, 64, 256, 280]
    },
    overlay: portraitOverlay
  }),
  SEARCH: Format.create({
    label: "search",
    width: 288,
    height: 224,
    grid: {
      rows: 74.6666667,
      columns: 96
    },
    guides: {
      safearea: [56, 8, 176, 112]
    },
    overlay: searchOverlay
  })
};

const tilesArr = objToArray(tiles);
const emailAvatarArray = objToArray(email_avatar);

const removeFileExtension = (filename) => {
  return filename.replace(/\.[^.]*$/, "");
};

export default function Preview({ image, mode }) {
  // Do some funky image manipulation here

  const [settings, setSettings] = useState({
    keypoint: [0.5, 0.5],
    source: {
      width: 0,
      height: 0,
      data: image
    },
    showGrid: false,
    showSafeareas: false,
    showOverlay: false
  });

  const [activePanelIndex, setPanelIndex] = useState(0);

  const imageRef = useRef(null);

  /**
   * [ ] Preview each Image format
   * [ ] Change crop and move
   * [ ] Save and export
   * [ ] Export compress and zip
   */

  const setKeyPoint = (point) => {
    const x = clamp(0, 1, point[0]);
    const y = clamp(0, 1, point[1]);

    setSettings((state) => ({ ...state, keypoint: [x, y] }));
  };

  const toggleGrid = () => {
    setSettings((state) => {
      const value = !state.showGrid;

      EventManager.emit("toggleGrid", { value });

      return { ...state, showGrid: value };
    });
  };

  const toggleSafeareas = () => {
    setSettings((state) => {
      const value = !state.showSafeareas;

      EventManager.emit("toggleSafeareas", { value });

      return { ...state, showSafeareas: value };
    });
  };

  const toggleOverlay = () => {
    setSettings((state) => {
      const value = !state.showOverlay;

      EventManager.emit("toggleOverlay", { value });

      return { ...state, showOverlay: value };
    });
  };

  useEffect(() => {
    if (!imageRef.current) {
      imageRef.current = new Image();
    }

    // Notify when image is loaded
    imageRef.current.onload = function () {
      setSettings((state) => ({
        ...state,
        source: {
          width: imageRef.current.width,
          height: imageRef.current.height,
          filename: removeFileExtension(image.file.name),
          file: image.file
        }
      }));
    };

    imageRef.current.src = image.data_url;

    // Load image
  }, [image]);

  const { source, showGrid, showSafeareas, showOverlay } = settings;

  const GridIcon = showGrid ? MdGridOff : MdGridOn;
  const GuidesIcon = showSafeareas ? MdNotInterested : MdFeaturedVideo;
  const OverlayIcon = showOverlay ? MdNotInterested : MdViewQuilt;

  // const activePanel = formatsArr[activePanelIndex];
  // const config = { ...activePanel, ...settings };

  // const next = () => {
  //   setPanelIndex((state) => {
  //     return (state + 1) % formatsArr.length;
  //   });
  // };

  // const previous = () => {
  //   setPanelIndex((state) => {
  //     return state === 0 ? formatsArr.length - 1 : state - 1;
  //   });
  // };

  const arr = mode === "tiles" ? tilesArr : emailAvatarArray;

  return (
    <div>
      <Title>
        <MdImage className="icon" /> {source.filename}
      </Title>
      <StyledActions>
        <button onClick={toggleGrid}>
          <GridIcon className="icon" />
        </button>
        <button onClick={toggleSafeareas}>
          <GuidesIcon className="icon" />
        </button>
        <button onClick={toggleOverlay}>
          <OverlayIcon className="icon" />
        </button>
      </StyledActions>
      {/* <Tabs
        items={formatsArr}
        onChange={setPanelIndex}
        activeIndex={activePanelIndex}
      /> */}
      <PreviewContainer>
        {imageRef.current !== null &&
          arr.map((item, idx) => {
            const config = {
              ...item,
              source: settings.source,
              raw: settings.raw
            };

            return (
              <Canvas
                key={item.label + idx}
                id={item.label + idx}
                image={imageRef.current}
                config={config}
                setKeypoint={setKeyPoint}
              />
            );
          })}
      </PreviewContainer>
      {/* <StyledContainer>
        <button onClick={previous}>
          <MdArrowBack className="icon" />
        </button>
        <div>
          <Pagination>
            {formatsArr.map((value, idx) => (
              <Bullet className={idx === activePanelIndex ? "active" : ""} />
            ))}
          </Pagination>
        </div>
        <button onClick={next}>
          <MdArrowForward className="icon" />
        </button>
      </StyledContainer> */}
    </div>
  );
}
